import React from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Text,
  VStack,
  HStack,
  Box,
  IconButton,
  Divider,
} from "@chakra-ui/react";
import { CloseIcon } from "@chakra-ui/icons";
import { FiExternalLink } from "react-icons/fi";

const JobDescriptionModal = ({ isOpen, onClose, application }) => {
  if (!application) return null;

  const handleOpenOriginal = () => {
    if (application.jobPostingUrl) {
      window.open(application.jobPostingUrl, '_blank');
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg" isCentered>
      <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(4px)" />
      <ModalContent
        borderRadius="xl"
        boxShadow="0 20px 60px rgba(0, 0, 0, 0.15)"
        maxW="600px"
        maxH="90vh"
        mx={4}
        display="flex"
        flexDirection="column"
      >
        <ModalHeader pb={2}>
          <HStack justify="space-between" align="flex-start">
            <VStack align="flex-start" spacing={1}>
              <Text fontSize="xl" fontWeight="700" color="gray.900" lineHeight="1.2">
                {application.roleTitle}
              </Text>
              <Text fontSize="md" color="gray.500" fontWeight="500">
                {application.companyName}
              </Text>
            </VStack>
            <IconButton
              icon={<CloseIcon />}
              variant="ghost"
              size="sm"
              onClick={onClose}
              color="gray.400"
              _hover={{ color: "gray.600", bg: "gray.100" }}
              borderRadius="lg"
            />
          </HStack>
        </ModalHeader>

        <ModalBody py={4} flex="1" overflowY="auto">
          <VStack align="stretch" spacing={6}>
            {/* Location Section */}
            <Box>
              <Text
                fontSize="sm"
                fontWeight="600"
                color="gray.600"
                textTransform="uppercase"
                letterSpacing="0.05em"
                mb={2}
              >
                Location
              </Text>
              <Text fontSize="md" color="gray.700" fontWeight="500">
                {application.jobLocationCity || "Not specified"}
              </Text>
            </Box>

            <Divider borderColor="gray.200" />

            {/* Application Details Section */}
            <Box>
              <Text
                fontSize="sm"
                fontWeight="600"
                color="gray.600"
                textTransform="uppercase"
                letterSpacing="0.05em"
                mb={3}
              >
                Application Details
              </Text>
              
              <VStack align="stretch" spacing={3}>
                <HStack justify="space-between">
                  <Text fontSize="sm" color="gray.600" fontWeight="500">
                    Date Applied:
                  </Text>
                  <Text fontSize="sm" color="gray.800" fontWeight="500">
                    {application.applicationDate 
                      ? new Date(application.applicationDate).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric'
                        })
                      : "Not specified"
                    }
                  </Text>
                </HStack>
                
                <HStack justify="space-between">
                  <Text fontSize="sm" color="gray.600" fontWeight="500">
                    Status:
                  </Text>
                  <Text 
                    fontSize="sm" 
                    color="gray.800" 
                    fontWeight="500"
                    textTransform="capitalize"
                  >
                    {application.status || "Applied"}
                  </Text>
                </HStack>
              </VStack>
            </Box>

            {/* Notes Section */}
            {application.notes && (
              <>
                <Divider borderColor="gray.200" />
                
                <Box>
                  <Text
                    fontSize="sm"
                    fontWeight="600"
                    color="gray.600"
                    textTransform="uppercase"
                    letterSpacing="0.05em"
                    mb={3}
                  >
                    Notes
                  </Text>
                  
                  <Box
                    bg="gray.50"
                    borderRadius="lg"
                    p={4}
                    border="1px solid"
                    borderColor="gray.200"
                  >
                    <Text
                      fontSize="sm"
                      color="gray.700"
                      lineHeight="1.6"
                      whiteSpace="pre-line"
                    >
                      {application.notes}
                    </Text>
                  </Box>
                </Box>
              </>
            )}

            <Divider borderColor="gray.200" />

            {/* Job Description Section */}
            <Box>
              <Text
                fontSize="sm"
                fontWeight="600"
                color="gray.600"
                textTransform="uppercase"
                letterSpacing="0.05em"
                mb={4}
              >
                Job Description
              </Text>
              
              <Box
                bg="gray.50"
                borderRadius="lg"
                p={4}
                border="1px solid"
                borderColor="gray.200"
                maxH="250px"
                overflowY="auto"
              >
                {application.jobDescription ? (
                  <Text
                    fontSize="sm"
                    color="gray.700"
                    lineHeight="1.6"
                    whiteSpace="pre-line"
                  >
                    {application.jobDescription}
                  </Text>
                ) : (
                  <Text fontSize="sm" color="gray.500" fontStyle="italic">
                    No job description available
                  </Text>
                )}
              </Box>
            </Box>
          </VStack>
        </ModalBody>

        <ModalFooter pt={6} flexShrink={0}>
          <HStack spacing={3} w="full">
            <Button
              leftIcon={<FiExternalLink />}
              bg="blue.500"
              color="white"
              size="md"
              borderRadius="lg"
              fontWeight="600"
              flex={1}
              _hover={{ bg: "blue.600" }}
              _active={{ bg: "blue.700" }}
              onClick={handleOpenOriginal}
              isDisabled={!application.jobPostingUrl}
            >
              Open Original Posting
            </Button>
            <Button
              variant="outline"
              size="md"
              borderRadius="lg"
              fontWeight="600"
              onClick={onClose}
              borderColor="gray.300"
              _hover={{ bg: "gray.50" }}
            >
              Close
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default JobDescriptionModal;